import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Info, X } from 'lucide-react';
interface InfoPopoverProps {
  label: string;
  title: string;
  body: string;
}
function InfoPopover({
  label,
  title,
  body
}: InfoPopoverProps) {
  const [open, setOpen] = useState(false);
  return <span className="relative inline-flex" data-id="element-190">
      <button type="button" onClick={() => setOpen(v => !v)} aria-label={`What is ${label}?`} className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#6C2BD9]/10 hover:bg-[#6C2BD9]/20 text-[#6C2BD9] transition-colors" data-id="element-191">
        <Info className="w-3 h-3" data-id="element-192" />
      </button>
      <AnimatePresence data-id="element-193">
        {open && <>
            {/* invisible click-away layer */}
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} data-id="element-194" />
            <motion.div initial={{
          opacity: 0,
          y: 4,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 4,
          scale: 0.96
        }} transition={{
          duration: 0.15
        }} className="absolute z-40 left-0 top-7 w-64 rounded-xl bg-white shadow-xl border border-slate-200 p-3 text-left" data-id="element-195">
              <div className="flex items-start justify-between gap-2" data-id="element-196">
                <p className="text-xs font-bold text-[#6C2BD9] uppercase tracking-wider" data-id="element-197">
                  {title}
                </p>
                <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Close" data-id="element-198">
                  <X className="w-3.5 h-3.5" data-id="element-199" />
                </button>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mt-1.5" data-id="element-200">
                {body}
              </p>
            </motion.div>
          </>}
      </AnimatePresence>
    </span>;
}
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-201">
      <div className="flex items-center justify-between" data-id="element-202">
        <label className="text-sm font-medium text-slate-600" data-id="element-203">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-204" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-205" />
    </div>;
}
type MasterType = 'teaser' | 'trailer';
const MASTER_PRICES: Record<MasterType, number> = {
  teaser: 3000,
  trailer: 6500
};
// Localization cost depends on the type: a teaser localizes for €999; a
// trailer localizes for €1,999 (which already includes a teaser).
const LOCALIZATION_PRICES: Record<MasterType, number> = {
  teaser: 999,
  trailer: 1999
};
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(2);
  const [countries, setCountries] = useState(2);
  const [showAnnualCost, setShowAnnualCost] = useState(false);
  const [hasMaster, setHasMaster] = useState(true);
  const [masterType, setMasterType] = useState<MasterType>('teaser');
  // List-price unit economics
  // Insights & optimization run for a minimum of 3 months.
  const campaignDuration = 3;
  // Localization always applies (you localize whether or not we create the
  // master for you) and is priced by type — teaser €999, trailer €1,599.
  const localizationCostBase = LOCALIZATION_PRICES[masterType];
  const monthlyInsightsFeeBase = 349;
  // Hosting is billed monthly per country as part of country activation.
  const monthlyHostingFee = 99;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  // 1) Country localization: 2 countries → 0% off (€999). 50+ countries → 50% off (€500). Linear.
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost;
  // 2) Master Snak: included or not, then teaser (€3,000) vs trailer (€6,500).
  const effectiveMasterSnakCost = hasMaster ? MASTER_PRICES[masterType] : 0;
  const masterSnakCost = effectiveMasterSnakCost;
  // 3) Data insights & hosting (monthly): 2 countries → €99/mo. 50+ countries → 50% off (€49.50/mo). Linear.
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const hostingPerCampaign = monthlyHostingFee * campaignDuration * countries;
    const deploymentPerCampaign = countryDeploymentCost * countries + hostingPerCampaign;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const rebateAmount = rawCostPerCampaign * durationRebate;
    const costAfterRebate = rawCostPerCampaign - rebateAmount;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costAfterRebate,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, monthlyHostingFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-206">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-207">
        {/* Header */}
        <div className="mb-6" data-id="element-208">
          <div className="flex items-center gap-3 mb-1" data-id="element-209">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-210">
              Snakker
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-211">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-212">™</sup>{' '}
            Cost Calculator
          </h1>
          <p className="text-base text-slate-500 mt-1" data-id="element-213">
            Estimate your annual campaign operating cost
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-214">
            Adjust the variables to see how your portfolio scales across brands,
            campaigns, countries, and insights.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-215">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-216">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-217">
              <div data-id="element-218">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-219">
                  Unit Economics
                </h3>
                <p className="text-[11px] text-[#6C2BD9]/60 mt-0.5" data-id="element-220">
                  Fixed Snakker pricing — everything below scales from these
                  unit costs.
                </p>
              </div>

              {/* Step 1 — do we create the master, or do you already have one? */}
              <div data-id="element-221">
                <p className="text-[11px] font-semibold text-[#6C2BD9]/70 uppercase tracking-wider mb-1.5" data-id="element-222">
                  1 · The Master
                </p>
                <div className="flex gap-2" data-id="element-223">
                  {[{
                  value: true,
                  label: 'Create it'
                }, {
                  value: false,
                  label: 'I have one'
                }].map(({
                  value,
                  label
                }) => {
                  const active = hasMaster === value;
                  return <button key={label} type="button" onClick={() => setHasMaster(value)} aria-pressed={active} className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors border-2 ${active ? 'bg-[#6C2BD9] text-white border-[#6C2BD9]' : 'bg-white text-[#6C2BD9] border-[#6C2BD9]/20 hover:border-[#6C2BD9]/40'}`} data-id="element-224">
                        {label}
                      </button>;
                })}
                </div>
                <p className="text-[11px] text-slate-400 mt-1" data-id="element-225">
                  {hasMaster ? 'We create the master, then localize it everywhere.' : 'You already have a master — we only localize it.'}
                </p>
              </div>

              {/* Step 2 — teaser or trailer (drives master + localization pricing) */}
              <div data-id="element-226">
                <p className="text-[11px] font-semibold text-[#6C2BD9]/70 uppercase tracking-wider mb-1.5" data-id="element-227">
                  2 · Type
                </p>
                <div className="flex gap-2" data-id="element-228">
                  {[{
                  type: 'teaser' as MasterType,
                  label: 'Teaser'
                }, {
                  type: 'trailer' as MasterType,
                  label: 'Trailer'
                }].map(({
                  type,
                  label
                }) => {
                  const active = masterType === type;
                  return <button key={type} type="button" onClick={() => setMasterType(type)} aria-pressed={active} className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors border-2 ${active ? 'bg-[#6C2BD9] text-white border-[#6C2BD9]' : 'bg-white text-[#6C2BD9] border-[#6C2BD9]/20 hover:border-[#6C2BD9]/40'}`} data-id="element-229">
                        {label}
                      </button>;
                })}
                </div>
              </div>

              {/* Master Snak — price set by selected mode */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-230">
                <div className="flex items-center justify-between" data-id="element-231">
                  <div data-id="element-232">
                    <div className="flex items-center gap-1.5" data-id="element-233">
                      <p className="text-sm font-semibold text-slate-700" data-id="element-234">
                        Master Snak
                      </p>
                      <InfoPopover label="Master Snak" title="What is a Master Snak?" body="We start by creating a Master Snak presentation — the original parent version of the campaign. All localized versions are derived from this single source of truth." data-id="element-235" />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-236">
                      {!hasMaster ? 'You already have a master' : `${masterType === 'teaser' ? 'Teaser' : 'Trailer'} master · per campaign`}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-237">
                    {!hasMaster ? <p className="text-2xl font-bold text-slate-400 tracking-tight" data-id="element-238">
                        —
                      </p> : <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-239">
                        €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                      </p>}
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-240">
                  {hasMaster && masterType === 'teaser' && 'Teaser master — €3,000 per campaign creation.'}
                  {hasMaster && masterType === 'trailer' && 'Trailer master — €6,500 per campaign creation.'}
                  {!hasMaster && 'No creation cost — you supply the master, we localize it.'}
                </p>
              </div>

              {/* Cost per country localization — with volume discount */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-241">
                <div className="flex items-center justify-between" data-id="element-242">
                  <div data-id="element-243">
                    <div className="flex items-center gap-1.5" data-id="element-244">
                      <p className="text-sm font-semibold text-slate-700" data-id="element-245">
                        Country activation
                      </p>
                      <InfoPopover label="Localization" title="What is localization?" body="Localization adapts the Master version to local usage, language and regulatory requirements — so the campaign is market-ready in every country it ships to." data-id="element-246" />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-247">
                      One-time set-up per country
                    </p>
                  </div>
                  <div className="text-right" data-id="element-248">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-249">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-250">
                      + €{monthlyHostingFee} / month hosting
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-251">
                  Country adaptation including regulatory rules, language and
                  Call-to-Actions.
                </p>
              </div>

              {/* Data insights & hosting — with country-volume discount */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-252">
                <div className="flex items-center justify-between" data-id="element-253">
                  <div data-id="element-254">
                    <div className="flex items-center gap-1.5" data-id="element-255">
                      <p className="text-sm font-semibold text-slate-700" data-id="element-256">
                        Campaign management
                      </p>
                      <InfoPopover label="Campaign management" title="What is campaign management?" body="Based on individual HCP engagement, we bi-monthly deliver not only real-world data but also actionable suggestions for improvement — so every campaign keeps getting sharper. We recommend keeping HCP access live for a minimum of 3 months after a campaign finishes." data-id="element-257" />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-258">
                      Data monitoring & impact optimization
                    </p>
                  </div>
                  <div className="text-right" data-id="element-259">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-260">
                      €
                      {(effectiveMonthlyInsightsFee * campaignDuration).toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-261">
                        / 3 mo
                      </span>
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-262">
                      €{effectiveMonthlyInsightsFee.toFixed(0)} / month
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-263">
                  Billed across a 3-month minimum window. Volume benefit:
                  discount slides from 0% at 2 countries up to 50% at 50+
                  countries.
                </p>
              </div>
            </div>

            {/* Portfolio Scale */}
            <div className="bg-slate-50 rounded-2xl p-5 space-y-4 border border-slate-200" data-id="element-264">
              <div data-id="element-265">
                <div className="flex items-center gap-1.5" data-id="element-266">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-267">
                    Portfolio Scale
                  </h3>
                  <InfoPopover label="Portfolio Scale" title="How to use this" body="Move the sliders to see the immediate impact on cost per localized campaign — including the cost of the Master Snak amortized across all localizations." data-id="element-268" />
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-269">
                  Adjust your portfolio size to see how cost scales.
                </p>
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={40} data-id="element-270" />
              <SliderInput label="Campaigns per Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={1} max={6} data-id="element-271" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={75} data-id="element-272" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-273">
            {/* Main number — cost per localized campaign */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-274">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-275">
                Cost per Localized Campaign
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-276">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-277">
                per managed and optimized local campaign
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-278">
              <div className="flex items-center justify-between mb-3" data-id="element-279">
                <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest" data-id="element-280">
                  Campaign Volume
                </p>
                <button type="button" onClick={() => setShowAnnualCost(v => !v)} aria-label="Toggle estimated annual cost" className="w-2 h-2 rounded-full bg-[#6C2BD9]/20 hover:bg-[#6C2BD9]/60 transition-colors" data-id="element-281" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-282">
                <div data-id="element-283">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-284">
                    {products}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-285">
                    {products === 1 ? 'brand' : 'brands'}
                  </p>
                </div>
                <div data-id="element-286">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-287">
                    {countries}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-288">
                    {countries === 1 ? 'country' : 'countries'}
                  </p>
                </div>
                <div data-id="element-289">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-290">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-291">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-292">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-293">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-294">
                    localized deployments / year
                  </p>
                </div>
              </div>
              {showAnnualCost && <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-baseline justify-between" data-id="element-295">
                  <p className="text-xs text-slate-500" data-id="element-296">
                    Estimated annual cost
                  </p>
                  <p className="text-base font-semibold text-slate-700" data-id="element-297">
                    {formatEuro(calc.totalAnnualCost)}
                    <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-298">
                      / year
                    </span>
                  </p>
                </div>}
            </div>

            {/* Breakdown — only visible when annual cost is revealed */}
            {showAnnualCost && <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200" data-id="element-299">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-300">
                  Cost Breakdown
                </p>
                <div className="space-y-2" data-id="element-301">
                  <div className="flex justify-between items-center" data-id="element-302">
                    <span className="text-sm text-slate-600" data-id="element-303">
                      Avg. cost per localized campaign
                    </span>
                    <span className="text-sm font-semibold text-slate-900" data-id="element-304">
                      {formatEuro(calc.costPerDeployment)}
                    </span>
                  </div>
                  <div className="h-px bg-slate-200 my-1" data-id="element-305" />
                  {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-306">
                      <span className="text-sm text-slate-500" data-id="element-307">
                        New masters ({calc.firstCampaigns} ×{' '}
                        {formatEuro(masterSnakCost)})
                      </span>
                      <span className="text-sm text-slate-700" data-id="element-308">
                        {formatEuro(calc.firstCampaigns * masterSnakCost)}
                      </span>
                    </div>}
                  {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-309">
                      <span className="text-sm text-slate-500" data-id="element-310">
                        Follow-up campaigns ({calc.followUpCampaigns} × €1,000)
                      </span>
                      <span className="text-sm text-slate-700" data-id="element-311">
                        {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center" data-id="element-312">
                    <span className="text-sm text-slate-500" data-id="element-313">
                      Country activation & hosting
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-314">
                      {formatEuro(calc.annualDeploymentCost)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-315">
                    <span className="text-sm text-slate-500" data-id="element-316">
                      Campaign management ({campaignDuration}mo)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-317">
                      {formatEuro(calc.annualInsightsCost)}
                    </span>
                  </div>
                </div>
              </div>}

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-4 border border-[#6C2BD9]/10" data-id="element-318">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-319">
                This is not software pricing. This is the annual cost of a fully
                managed campaign operating model across your portfolio.
              </p>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-320">
              As you scale across more products, campaigns, or countries, cost
              grows predictably — while execution stays centralized.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-321">
          <Button onClick={onContinue} data-id="element-322">Thank You</Button>
        </div>
      </div>
    </div>;
}